import axios, { AxiosRequestConfig } from "axios";
import { EquipmentCategory, ObjectToEquipmentCategory } from "../Objects/EquipmentCategory";
import { EquipmentData, ObjectToEquipmentData } from "../Objects/EquipmentData";
import { EquipmentGroup, ObjectToEquipmentGroup } from "../Objects/EquipmentGroup";
import { ApiError } from "./ApiError";

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-Token';

export interface getEquipmentCategoryListResults {
    list: EquipmentCategory[]
}
export const getEquipmentCategoryList = async (config?: AxiosRequestConfig): Promise<getEquipmentCategoryListResults> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/get_equipment_category_list', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            list: res.data.list.map((item: any) => ObjectToEquipmentCategory(item))
        }
    } catch (error) {
        throw error
    }
}

export interface getEquipmentListProps {
    str_id: number,
    equcate_id: number
}

export interface getEquipmentListResults {
    list: EquipmentData[]
}

export const getEquipmentList = async ({
    str_id,
    equcate_id
}: getEquipmentListProps, config?: AxiosRequestConfig): Promise<getEquipmentListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString());
        params.append("equcate_id", equcate_id.toString());
        params.append("with_available_studio", "true");

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/get_equipment_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            list: res.data.list.map((item: any) => ObjectToEquipmentData(item))
        }
    } catch (error) {
        throw error
    }
}

export interface getEquipmentGroupListProps {
    str_id: number
}

export interface getEquipmentGroupListResults {
    list: EquipmentGroup[]
}

export const getEquipmentGroupList = async ({
    str_id,
}: getEquipmentGroupListProps, config?: AxiosRequestConfig): Promise<getEquipmentGroupListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("str_id", str_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/get_equipment_group_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            list: res.data.list.map((item: any) => ObjectToEquipmentGroup(item))
        }
    } catch (error) {
        throw error
    }
}

export interface getEquipmentAvailableStudioListProps {
    equ_id: number
}

export interface getEquipmentAvailableStudioListResults {
    std_ids: number[]
}

export const getEquipmentAvailableStudioList = async ({
    equ_id,
}: getEquipmentAvailableStudioListProps, config?: AxiosRequestConfig): Promise<getEquipmentAvailableStudioListResults> => {
    try {
        let params = new URLSearchParams()
        params.append("equ_id", equ_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/get_equipment_available_studio_list',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
        return {
            std_ids: res.data.list.map((item: any) => parseInt(item.std_id))
        }
    } catch (error) {
        throw error
    }
}

export interface RegEquipmentDataProps {
    equipmentData: EquipmentData,
    reg_number?: number
}

export const RegEquipmentData = async ({
    equipmentData,
    reg_number
}: RegEquipmentDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if (!equipmentData.equcate_id) {
            throw new Error("機材カテゴリーが選択されていません");
        }
        params.append("equcate_id", equipmentData.equcate_id.toString());
        if (!equipmentData.equ_name) {
            throw new Error("機材名が入力されていません");
        }
        params.append("equ_name", equipmentData.equ_name);
        if (!equipmentData.str_id) {
            throw new Error("店舗が選択されていません");
        }
        params.append("str_id", equipmentData.str_id.toString());

        if (equipmentData.equ_maker) {
            params.append("equ_maker", equipmentData.equ_maker);
        }
        if (equipmentData.equ_model) {
            params.append("equ_model", equipmentData.equ_model);
        }
        if (equipmentData.equ_shortened) {
            params.append("equ_shortened", equipmentData.equ_shortened);
        }
        if (equipmentData.rental_flug) {
            params.append("rental_flug", equipmentData.rental_flug ? "true" : "false");
        }
        if (equipmentData.equ_charge_type) {
            params.append("equ_charge_type", equipmentData.equ_charge_type);
        }
        if (equipmentData.equ_charge) {
            params.append("equ_charge", equipmentData.equ_charge.toString());
        }
        params.append("equgrp_id", equipmentData.equgrp_id ? equipmentData.equgrp_id.toString() : "0");

        if (reg_number) {
            params.append("reg_number", reg_number.toString());
        }

        if (equipmentData.available_studio_ids) {
            params.append("available_studio_ids", equipmentData.available_studio_ids.join(","));
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/reg_equipment_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface EditEquipmentDataProps {
    equipmentData: EquipmentData,
}

export const EditEquipmentData = async ({
    equipmentData,
}: RegEquipmentDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        if (!equipmentData.equ_id) {
            throw new Error("機材IDが選択されていません");
        }
        params.append("equ_id", equipmentData.equ_id.toString());
        if (!equipmentData.equcate_id) {
            throw new Error("機材カテゴリーが選択されていません");
        }
        params.append("equcate_id", equipmentData.equcate_id.toString());
        if (!equipmentData.equ_name) {
            throw new Error("機材名が入力されていません");
        }
        params.append("equ_name", equipmentData.equ_name);
        if (!equipmentData.str_id) {
            throw new Error("店舗が選択されていません");
        }
        params.append("str_id", equipmentData.str_id.toString());

        if (equipmentData.equ_maker) {
            params.append("equ_maker", equipmentData.equ_maker);
        }
        if (equipmentData.equ_model) {
            params.append("equ_model", equipmentData.equ_model);
        }
        if (equipmentData.equ_shortened) {
            params.append("equ_shortened", equipmentData.equ_shortened);
        }
        if (equipmentData.rental_flug) {
            params.append("rental_flug", equipmentData.rental_flug ? "true" : "false");
        }
        if (equipmentData.equ_charge_type) {
            params.append("equ_charge_type", equipmentData.equ_charge_type);
        }
        if (equipmentData.equ_charge) {
            params.append("equ_charge", equipmentData.equ_charge.toString());
        }
        params.append("equgrp_id", equipmentData.equgrp_id ? equipmentData.equgrp_id.toString() : "0");

        if (equipmentData.available_studio_ids) {
            params.append("available_studio_ids", equipmentData.available_studio_ids.join(","));
        }

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/edit_equipment_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface RegRepairedEquipmentProps {
    equ_id: number
}

export const RegRepairedEquipment = async ({
    equ_id
}: RegRepairedEquipmentProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("equ_id", equ_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/reg_repaired_equipment',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface RegRepairedCompleteEquipmentProps {
    equ_id: number
}

export const RegRepairedCompleteEquipment = async ({
    equ_id
}: RegRepairedCompleteEquipmentProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("equ_id", equ_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/reg_repaired_complete_equipment',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export interface DeleteEquipmentDataProps {
    equ_id: number
}

export const DeleteEquipmentData = async ({
    equ_id
}: DeleteEquipmentDataProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append("equ_id", equ_id.toString());

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'equipment/delete_equipment_data',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError(res.data.message ? res.data.message : "サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}